import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { executionDependencyNames } from '@de/utils/data-sources/readyWhen';

/**
 * Widen `names` to include every source they transitively depend on.
 *
 * This exists because `dependencyLevels` deliberately ignores dependencies
 * outside the requested subset, which is the right default for a caller that
 * already knows its subset is self-contained -- but leaves a caller that
 * selected sources indirectly, by tag, with no way to know whether it did. Such
 * a caller re-runs a source whose dependency stays as it last resolved, so the
 * re-run interpolates a stale value and still reports success. Passing the
 * closure instead makes the dependency re-run first.
 *
 * Deliberately not folded into `dependencyLevels`, and not applied to
 * `refetchByTags` unconditionally. Whether an out-of-subset dependency *should*
 * re-run is a question about what that source is for, which is exactly what the
 * manager declines to decide: it matches tags and never interprets them. A
 * dependency that feeds an authoring control wants to be left alone, so
 * re-running it would reset a dropdown under the author mid-edit; a dependency
 * that feeds a request body wants to re-run. Only the host knows which it has,
 * so the host opts in and the graph walk lives here.
 *
 * Order follows `ordered`, so the result is deterministic for a given graph, and
 * a dependency cycle terminates rather than looping -- each name is expanded at
 * most once. Names absent from `dataSources` are dropped, matching the
 * no-op-on-unknown-name contract of every other fetch path.
 */
export function dependencyClosure(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  ordered: readonly string[],
  names: readonly string[],
): readonly string[] {
  const known = (name: string): boolean =>
    Object.prototype.hasOwnProperty.call(dataSources, name);

  const reached = new Set<string>();
  const pending = names.filter(known);

  while (pending.length > 0) {
    const name = pending.pop();
    if (name === undefined || reached.has(name)) {
      continue;
    }
    reached.add(name);
    for (const dependency of executionDependencyNames(dataSources[name])) {
      if (known(dependency) && !reached.has(dependency)) {
        pending.push(dependency);
      }
    }
  }

  if (reached.size === 0) {
    return [];
  }
  return ordered.filter((name) => reached.has(name));
}
